import { Avatar, Button, IconButton, useToast } from "@themis/ui";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { PiXBold } from "react-icons/pi";

import { Relatable, UpdateTaskRequest } from "@/api";
import {
  useCreateTask,
  useCreateTaskable,
  useDeleteTaskable,
  useTask,
  useUpdateTask,
} from "@/api/queries/tasks";
import { useLegacyCompanyUsers } from "@/api/queries/users";
import Loading from "@/components/Loading";
import { useMainStore } from "@/contexts/Store";

import TaskDetailForm from "./TaskDetailForm";

const SLIDEOUT_WIDTH = "500px";

function HeaderContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-px-7 tw-py-2.5">
      {children}
    </div>
  );
}
function SectionContainer({ children }: { children: React.ReactNode }) {
  return <div className="tw-px-7 tw-py-3">{children}</div>;
}

function TaskDetail() {
  const mainStore = useMainStore();
  const { taskDetail } = mainStore;
  const { workspaceID } = mainStore.context;
  const companyId = mainStore.companies.company?.id;
  const toast = useToast();
  const isArchived = Boolean(taskDetail.data.archived_at);

  const taskDetailContainer = useRef<HTMLDivElement>(null);
  const [formDidChange, setFormDidChange] = useState(false);

  const { data: users, isSuccess: isUsersSuccess } = useLegacyCompanyUsers(
    Number(workspaceID),
  );
  const { data: task, isSuccess: isTaskSuccess } = useTask(
    Number(companyId),
    Number(taskDetail.data.id),
  );
  const isSuccess = isUsersSuccess && isTaskSuccess;
  const createdByUser = task
    ? users?.data.users.find(
        (user) => user.id === task?.data.created_by_id,
      ) || {
        full_name: "Not Found",
        initials: "NF",
        icon_color_index: 0,
      }
    : mainStore.users.user;

  const { mutateAsync: updateTask } = useUpdateTask({
    companyId: Number(companyId),
    taskId: Number(taskDetail.data.id),
  });
  const { mutateAsync: createTask } = useCreateTask({
    companyId: Number(companyId),
  });
  const { mutateAsync: createTaskable } = useCreateTaskable({
    companyId: Number(companyId),
    taskId: Number(taskDetail.data.id),
  });
  const { mutateAsync: deleteTaskable } = useDeleteTaskable({
    companyId: Number(companyId),
    taskId: Number(taskDetail.data.id),
  });

  useEffect(() => {
    if (!taskDetail.data.id) {
      createNewTask();
    }
  }, [taskDetail.data.id]);

  const uniqueUsers = users?.data.users.filter(
    (user, index, self) =>
      index === self.findIndex((t) => t.email === user.email),
  );

  async function createNewTask() {
    try {
      const { data: createdTask } = await createTask({
        task: {
          ...taskDetail.data,
          name: taskDetail.data.name || "My Task",
          taskables: taskDetail.data.taskables?.map((taskable) => ({
            taskable_id: taskable.target?.id || 0,
            taskable_type: "Record",
          })),
          assignee_id: mainStore.users.user?.id,
        },
      });
      // This updates store with task id so that this component can fetch task accordingly
      taskDetail.open(createdTask);
      toast({
        content: "Task created successfully!",
        variant: "success",
      });
    } catch {
      toast({
        content: "Something went wrong. Could not create task.",
        variant: "error",
      });
      taskDetail.close();
    }
  }

  const handleClose = () => {
    taskDetail.close();
  };

  const handleUpdateTask = async (
    updatedValue: Partial<UpdateTaskRequest["task"]>,
  ) => {
    try {
      await updateTask({
        task: {
          ...updatedValue,
        },
      });

      toast({ content: "Task updated successfully!", variant: "success" });
    } catch {
      toast({
        content: "Something went wrong. Could not update task.",
        variant: "error",
      });
    }
  };

  async function handleRecordSelect(record: Relatable) {
    try {
      await createTaskable({
        taskable: {
          target_id: record.id,
          target_type: "Record",
        },
      });
      toast({
        content: "Associated Record added to Task successfully!",
        variant: "success",
      });
    } catch {
      toast({
        content: "Something went wrong. Could not update task.",
        variant: "error",
      });
    }
  }

  async function handleRecordDelete(id: number) {
    try {
      await deleteTaskable(id);
      toast({
        content: "Associated Record deleted successfully!",
        variant: "success",
      });
    } catch {
      toast({
        content: "Something went wrong. Could not update task.",
        variant: "error",
      });
    }
  }

  const handleToggleArchive = async () => {
    try {
      await updateTask({
        task: {
          archived_at: isArchived ? null : new Date().toISOString(),
        },
      });
      toast({
        content: isArchived
          ? "Task unarchived successfully!"
          : "Task archived successfully!",
        variant: "success",
      });
      taskDetail.close();
    } catch {
      toast({
        content: isArchived
          ? "Something went wrong. Could not unarchive task."
          : "Something went wrong. Could not archive task.",
        variant: "error",
      });
    }
  };

  const handleMarkComplete = async () => {
    try {
      await updateTask({
        task: {
          status: "Done",
        },
      });
      toast({ content: "Task completed!", variant: "success" });
      taskDetail.close();
    } catch {
      toast({
        content: "Something went wrong. Could not update task.",
        variant: "error",
      });
    }
  };

  return (
    <div
      ref={taskDetailContainer}
      className="tw-absolute tw-inset-y-0 tw-right-0 tw-z-[101] tw-flex tw-flex-col tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-neutral-100 tw-overflow-y-auto tw-bg-white tw-shadow-slideout"
      style={{ width: SLIDEOUT_WIDTH }}
    >
      <HeaderContainer>
        <h3 className="tw-text-base tw-font-semibold tw-text-neutral-500">
          Task Detail
        </h3>
        <IconButton onClick={handleClose} color="transparent" Icon={PiXBold} />
      </HeaderContainer>

      <SectionContainer>
        <div className="tw-flex tw-items-center tw-justify-between ">
          {!isArchived && (
            <Button
              color="tertiary"
              size="md"
              disabled={task?.data.status === "Done"}
              onClick={handleMarkComplete}
            >
              Mark Complete
            </Button>
          )}
          <span className="tw-flex tw-items-center tw-space-x-3 tw-rounded-md tw-bg-neutral-50 tw-px-3 tw-py-1.5 tw-font-semibold">
            <div className="tw-font-semibold tw-text-neutral-500">
              Created by
            </div>
            <div className="tw-flex tw-items-center tw-space-x-1">
              <Avatar size="md" colorIndex={createdByUser.icon_color_index}>
                {createdByUser.initials}
              </Avatar>
              <span className="tw-text-neutral-300">
                {createdByUser.full_name}
              </span>
            </div>
          </span>
        </div>
      </SectionContainer>

      <SectionContainer>
        {isSuccess ? (
          <TaskDetailForm
            users={uniqueUsers || []}
            onFieldChange={handleUpdateTask}
            onClickArchive={handleToggleArchive}
            defaultValues={task?.data}
            formDidChange={formDidChange}
            setFormDidChange={setFormDidChange}
            onRecordSelect={handleRecordSelect}
            onRecordDelete={handleRecordDelete}
          />
        ) : (
          <Loading loadingLayout="single-file-form" />
        )}
      </SectionContainer>
    </div>
  );
}
export { SLIDEOUT_WIDTH };

export default observer(TaskDetail);
